/**
 * Everything on the maintenance page that differs between the ten apps.
 *
 * The Figma frames share a layout, but NOT as much as first assumed. Verified
 * deviations, all from node geometry across the 20 frames:
 *
 *  - Logo aspect ratios run from 3.33:1 (Jubilee) to 6.32:1 (AI Dropship)
 *    against AliDrop's 4.26:1, so the logo box cannot be one fixed size.
 *  - Spocket and DropshipTool place the background rings 109px further right
 *    than the other eight.
 *  - The divider and card border are the app's own headline ink at low alpha,
 *    not a shared neutral — that only looked neutral on AliDrop because its ink
 *    is #191919, i.e. rgb(25,25,25).
 *  - Spocket's pill border is 14%, not the 27% the other checked frames use.
 *
 * Copying this component to a sibling repo means editing this file and nothing
 * else. Values live in plans/maintenance-pages-all-apps/src/tokens.json.
 *
 * Smartli-specific notes (Figma node 150:2284, tokens.json `apps.smartli`):
 *  - Config profile A, same as AliDrop — glow geometry, ring desktop %, pill
 *    border alpha and font weights are all identical to the reference; only
 *    the accent colour, inks and headline/promo strings differ.
 *  - Logo aspect ratio (3.87:1) is Smartli's own, not AliDrop's 4.26:1 — logo
 *    box dimensions come from Smartli's own frame, both breakpoints.
 *  - Mobile ring offsets are NOT the same as AliDrop's (61.4/47.1 here vs
 *    51.2/36.8 there) — taken from tokens.json's per-app `rings.mobile`
 *    entry rather than assumed to match the desktop-identical profile.
 */

export type Promo = {
  name: string;
  headline: string;
  blurb: string;
  linkLabel: string;
  linkUrl: string;
};

export const DROPSHIPTOOL: Promo = {
  name: "Dropshiptool",
  headline: "Discover Winning Products and High-Performing Viral Ads",
  blurb:
    "Track over 3 million Shopify stores to uncover winning products, viral TikTok ads, and top-performing Facebook and Instagram creatives.",
  linkLabel: "dropshiptool.io",
  linkUrl:
    "https://app.dropshiptool.io/register?utm_source=smartli&utm_medium=maintenance&utm_campaign=findwinningads",
};

/** Identical on every frame — kept here so the ten copies can't drift. */
export const SHARED = {
  status: "Maintenance in progress",
  statusShort: "Maintenance",
  body: "We're running scheduled work on the platform. Nothing is lost, and your store keeps selling. We'll be back shortly.",
  cta: "Back to Home",
  reassurance: "This page refreshes itself when we're live.",
  promoEyebrow: "While you wait",
};

export const APP = {
  name: "Smartli",
  /** `${name} will be back soon.` — the only per-app string in the headline. */
  home: "/",
  promo: DROPSHIPTOOL,

  /** Headline ink. Also the source for the divider and card border. */
  ink: "#191919",
  /** Body, reassurance, eyebrow, blurb, pill label. */
  muted: "#838383",

  /**
   * Logo box, measured from Smartli's own frame. Rendered with
   * object-fit: contain, so a wrong value letterboxes rather than distorts.
   * `public/logo.svg` here is already the full icon+wordmark lockup (verified
   * by reading the file), so no separate maintenance-only asset was needed —
   * unlike AliDrop, whose `logo.svg` is icon-only.
   */
  logo: {
    src: "/logo.svg",
    width: 129.5,
    height: 33.45,
    mobileWidth: 104.1,
    mobileHeight: 26.87,
  },

  /** Ring left offsets as a percentage of frame width. Desktop matches AliDrop
   *  (781px/725px of 1440); mobile does NOT — Smartli's own frame places them
   *  at 61.4%/47.1%, not AliDrop's 51.2%/36.8%. */
  rings: { outer: 54.2, inner: 50.3, outerMobile: 61.4, innerMobile: 47.1 },

  /** Background glow. Radii are the SVG matrix scale x10 (gradientUnits is
   *  userSpaceOnUse with r=10). Same geometry as AliDrop; the colour comes
   *  from theme.colors.primary, so it tints blue here for free. */
  glow: { radiusX: 1570, radiusY: 734, atX: 93, mobileRadiusX: 426 },

  /** Status pill border alpha. Two-digit hex suffix. 45 = 27%, 24 = 14%. */
  pillBorderAlpha: "45",

  /**
   * Typography is per-app, not shared. Smartli uses Inter, same as the
   * reference — profile A weights, no change.
   */
  font: {
    family:
      '"Inter Variable", Inter, -apple-system, BlinkMacSystemFont, sans-serif',
    headline: 600,
    body: 400,
    cta: 700,
    reassurance: 300,
    eyebrow: 500,
    promoName: 600,
    promoHeadline: 600,
    promoBlurb: 400,
    promoLink: 600,
  },
};
